import {Component, OnInit} from '@angular/core';
import {gql} from '@apollo/client';
import {FormControl, FormGroup, Validators} from '@angular/forms';
import {Subject, Subscription} from 'rxjs';
import {CoachMutationInput} from '../../../shared/graphql/globalTypes';
import {catchError, exhaustMap} from 'rxjs/operators';
import {Language} from '../../../new-client/new-client.component';
import {Apollo} from 'apollo-angular';
import {TranslateService} from '@ngx-translate/core';
import {ToastrService} from 'ngx-toastr';
import {ActivatedRoute, Router} from '@angular/router';
import {
  NewCoachComponent_newCoach,
  NewCoachComponent_newCoachVariables,
} from './__generated__/NewCoachComponent_newCoach';
import { ErrorService } from 'src/app/shared/services/error.service';

const NEW_COACH = gql`
    mutation NewCoachComponent_newCoach($input: CoachMutationInput!) {
        createOrUpdateCoach(input: $input) {
          user {
            id
            firstName
            lastName
            email
          }
          errors {
            field
            messages
          }
        }
    }
`;
@Component({
  selector: 'app-new-coach',
  templateUrl: './new-coach.component.html',
  styleUrls: ['./new-coach.component.scss'],
})
export class NewCoachComponent implements OnInit {
  invalidSubmission = false;
  subscriptions: Subscription[] = [];
  signupForm = new FormGroup({
    email: new FormControl(null, [Validators.required, Validators.email]),
    firstName: new FormControl(null, Validators.required),
    lastName: new FormControl(null, Validators.required),
    language: new FormControl(null, Validators.required),
  });
  tenantId = '';

  emailErrorMessages: string[] = [];
  firstNameErrorMessages: string[] = [];
  lastNameErrorMessages: string[] = [];
  languages: Language[] = [
    Language.NL,
    // Language.EN,
    Language.DE,
  ];

  submit$ = new Subject<CoachMutationInput>();

  constructor(
    private apollo: Apollo,
    private translate: TranslateService,
    private toastr: ToastrService,
    private router: Router,
    private route: ActivatedRoute,
    private errorService: ErrorService
  ) {}

  ngOnInit(): void {
    this.tenantId = this.route.snapshot.paramMap.get('id') ?? '';

    const languageTranslationMap: {[key in Language]: string} = {
      [Language.NL]: this.translate.instant('NL', Language.NL),
      // [Language.EN]: this.translate.instant('ENGLISH', Language.EN),
      [Language.DE]: this.translate.instant('DE', Language.DE),
    };

    this.languages.forEach(lang => languageTranslationMap[lang]);

    if (localStorage.getItem('language') === 'nl') {
      this.signupForm.controls['language'].setValue(Language.NL);
    } else if (localStorage.getItem('language') === 'de') {
      this.signupForm.controls['language'].setValue(Language.DE);
    }

    this.subscriptions.push(
      this.submit$
        .pipe(
          exhaustMap(input => {
            return this.apollo
              .mutate<NewCoachComponent_newCoach, NewCoachComponent_newCoachVariables>({
                mutation: NEW_COACH,
                variables: {
                  input: {
                    ...input,
                    tenant: this.route.snapshot.paramMap.get('id') ?? '',
                  },
                },
                update: cache =>
                  cache.evict({id: 'TenantType:' + this.tenantId, fieldName: 'coaches'}),
              })
              .pipe(
                catchError(error => this.errorService.handleApollo(error)),
              );
          }),
        )
        .subscribe(result => {
          this.emailErrorMessages = [];
          this.firstNameErrorMessages = [];
          this.lastNameErrorMessages = [];
          const errors = result.data?.createOrUpdateCoach.errors;
          if (errors && errors.length > 0) {
            errors.forEach(e => {
              switch (e?.field) {
                case 'email':
                  this.emailErrorMessages = e?.messages;
                  break;
                case 'firstName':
                  this.firstNameErrorMessages = e?.messages;
                  break;
                case 'lastName':
                  this.lastNameErrorMessages = e?.messages;
                  break;
                default:
                  break;
              }
            });
            return;
          }

          this.toastr.success(this.translate.instant('COACH_ADDED'), '', {
            timeOut: 2000,
          });

          this.returnToPreviousPage();
        }),
    );
  }

  onSubmit(): void {
    if (this.signupForm.invalid) {
      this.invalidSubmission = true;
      return;
    }

    this.signupForm.controls['language'].setValue(
      this.signupForm.controls['language'].value.toLowerCase(),
    );
    this.submit$.next(this.signupForm.value);
    this.signupForm.controls['language'].setValue(
      this.signupForm.controls['language'].value.toUpperCase(),
    );
  }

  returnToPreviousPage() {
    const currentUrl = this.router.url;
    const parentUrl = currentUrl.substring(0, currentUrl.lastIndexOf('/new-coach'));
    this.router.navigateByUrl(parentUrl);
  }
}
